import { useState, useMemo, useEffect, useRef } from "react"
import TagBadge, { type Tag, type TaggableVariant } from "./TagBadge"
import { EditSkuTagsDialog } from "./EditSkuTagsDialog"
import { uniqBy } from "lodash"
import { filterDefined } from "~/utils/tsUtils"
import { setEditClickCallback } from "~/components/reusable/table/tooltips/TagsTooltip"
import { useBadgeOverflow } from "~/hooks/useBadgeOverflow"

type TagsCellRendererProps = {
  value: Tag[] | null | undefined
  entries: TaggableVariant[]
}

export function TagsCellRenderer({ value, entries }: TagsCellRendererProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const tags = useMemo(() => {
    if (!value) return []
    return uniqBy(filterDefined(value), (x) => x.id)
  }, [value])

  const skuIds = useMemo(() => entries.flatMap((entry) => entry.skuIds), [entries])
  const dialogKey = useMemo(() => skuIds.sort().join(","), [skuIds])

  useEffect(() => {
    setEditClickCallback(dialogKey, () => setIsEditDialogOpen(true))
    return () => {
      // Cleanup is handled by the module-level map
    }
  }, [dialogKey])

  const variantIds = useMemo(() => entries.map((entry) => entry.variantId), [entries])

  const sortedTags = useMemo(() => [...tags].sort((a, b) => a.name.localeCompare(b.name)), [tags])

  const { visibleCount } = useBadgeOverflow(sortedTags, containerRef)

  if (!entries.length) return null

  return (
    <>
      <div ref={containerRef}>
        <TagBadge tags={tags} entries={entries} count={visibleCount} inTable />
      </div>
      <EditSkuTagsDialog
        variantIds={variantIds}
        variantsOverride={entries}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
      />
    </>
  )
}
